import "./share-actions.scss";

import CrossIcon from "@frontend/images/icons/CrossIcon";
import { useMemo, useState, useEffect } from "react";


const DEFAULT_CHANNELS = [
    { id: "facebook", label: "Facebook" },
    { id: "twitter", label: "Twitter/X" },
    { id: "linkedin", label: "Linkedin" },
];

function buildShareUrl(channel, encodedUrl, encodedText) {
    if (typeof channel.url === "function") {
        return channel.url({ encodedUrl, encodedText });
    }

    if (channel.url) {
        return channel.url;
    }

    switch (channel.id) {
        case "facebook":
            return encodedText
                ? `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`
                : `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        case "twitter":
            return encodedText
                ? `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`
                : `https://twitter.com/intent/tweet?url=${encodedUrl}`;
        case "linkedin":
            return `https://www.linkedin.com/feed/?shareActive=true&text=${encodedUrl}`;
        default:
            return "";
    }
}

export default function ShareActions({
    title = "Share",
    copyLabel = "Copy link",
    copiedLabel = "Link copied",
    shareText = "",
    url,
    channels = DEFAULT_CHANNELS,
    baseClassName = "sharedShare",
}) {
    const [isCopyTooltipVisible, setIsCopyTooltipVisible] = useState(false);

    useEffect(() => {
        if (!isCopyTooltipVisible) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setIsCopyTooltipVisible(false);
        }, 5000);

        return () => window.clearTimeout(timeoutId);
    }, [isCopyTooltipVisible]);
    const rootClassName =
        baseClassName === "sharedShare"
            ? "sharedShare"
            : `sharedShare ${baseClassName}`;

    const listClassName =
        baseClassName === "sharedShare"
            ? "sharedShare__list"
            : `sharedShare__list ${baseClassName}__list`;

    const currentUrl = useMemo(
        () =>
            url || (typeof window !== "undefined" ? window.location.href : ""),
        [url],
    );

    const encodedUrl = useMemo(
        () => encodeURIComponent(currentUrl),
        [currentUrl],
    );
    const encodedText = useMemo(
        () => encodeURIComponent(shareText),
        [shareText],
    );
    const isMobile =
        typeof window !== "undefined" &&
        /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

    function handleCopyLink() {
        navigator.clipboard.writeText(currentUrl);
        setIsCopyTooltipVisible(true);
    }

    function openShare(shareUrl) {
        if (!shareUrl) {
            return;
        }

        if (isMobile) {
            window.location.href = shareUrl;
            return;
        }

        window.open(shareUrl, "_blank");
    }

    function handleChannelClick(channel) {
        const shareUrl = buildShareUrl(channel, encodedUrl, encodedText);
        openShare(shareUrl);
    }

    return (
        <div className={rootClassName}>
            <span>{title}</span>
            <div className={listClassName}>
                <button onClick={handleCopyLink}>
                    {copyLabel}
                    {isCopyTooltipVisible && (
                        <div
                            className="sharedShare__tooltip"
                            role="status"
                            aria-live="polite"
                        >
                            <span>{copiedLabel}</span>
                            <div
                                className="sharedShare__tooltip__close"
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setIsCopyTooltipVisible(false);
                                }}
                                aria-label="Close tooltip"
                            >
                                <CrossIcon />
                            </div>
                        </div>
                    )}
                </button>
                {channels.map((channel) => (
                    <button
                        key={channel.id}
                        onClick={() => handleChannelClick(channel)}
                    >
                        {channel.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
